import { Stack, Tooltip } from '@mui/material';
import isNil from 'lodash/isNil';
import { ReactNode } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { PTypographyLoading } from '@/components/molecules/TypographyLoading/PTypographyLoading';
import { TITLE_MAX_WIDTH } from '@/components/organisms/PageHeader/constants';
import { resolveTooltipText } from '@/components/organisms/PageHeader/utils';

type PageHeaderTitleProps = {
  /**
   * The title content.
   *
   * - When `children` is a **string or number**, `Title` wraps it in an h4
   *   `PTypographyLoading` and owns the heading typography, truncation, and
   *   skeleton loading.
   * - When `children` is a **component** (e.g. `BuildingSelector`), it is
   *   rendered as-is — `Title` does not wrap it in Typography (that would
   *   produce invalid HTML and override the component's styling). The
   *   component is responsible for its own h4-level typography and for
   *   handling its own loading/truncation.
   */
  children: ReactNode;
  /**
   * Optional status chip rendered inline, immediately after the title cluster.
   */
  statusBadge?: ReactNode;
  /**
   * Optional customer-internal identifier shown as muted caption text next to
   * the status badge.
   */
  internalReference?: string | null;
  /**
   * Whether the title is loading. Applies only when `children` is a string or
   * number; component children handle their own loading state.
   */
  isLoading?: boolean;
  /**
   * Full title text used for the hover tooltip. When `children` is a plain
   * string, `tooltip` defaults to it; pass `tooltip` explicitly when
   * `children` is a component (e.g. a selector) so the header can still show
   * the full title on hover.
   */
  tooltip?: string;
};

export const PageHeaderTitle = ({
  children,
  statusBadge,
  internalReference,
  isLoading,
  tooltip,
}: PageHeaderTitleProps) => {
  const tooltipText = resolveTooltipText(children, tooltip);
  const hasTooltip = !isNil(tooltipText);
  const hasInternalReference = !isNil(internalReference) && internalReference.length > 0;
  const isTextChildren = typeof children === 'string' || typeof children === 'number';

  const titleNode = isTextChildren ? (
    !isNil(isLoading) ? (
      <PTypographyLoading
        variant="h4"
        isLoading={isLoading}
        loaderWidth={240}
        noOverflow={{ maxWidth: TITLE_MAX_WIDTH, title: tooltipText ?? '' }}
      >
        {children}
      </PTypographyLoading>
    ) : (
      <PTypography variant="h4" noOverflow={{ maxWidth: TITLE_MAX_WIDTH, title: tooltipText ?? '' }}>
        {children}
      </PTypography>
    )
  ) : (
    children
  );

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
      {hasTooltip ? (
        <Tooltip title={tooltipText} arrow>
          <span>{titleNode}</span>
        </Tooltip>
      ) : (
        titleNode
      )}
      {statusBadge}
      {hasInternalReference ? (
        <PTypography variant="caption" color="secondary">
          {internalReference}
        </PTypography>
      ) : null}
    </Stack>
  );
};
